import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { formatBytes } from "kontrolEdge/components/ResourceManager/Srv6Insight/Srv6Types";

/**
 * A Virtual Firewall's IPsec tunnels, as the backend reduces them (CTX-882): what each
 * tunnel is and how it is doing — never who connects or from where. No user, peer IP or
 * subnet reaches the browser.
 */
export interface SiteToSiteTunnel {
    name: string;
    kind: "site-to-site";
    status: "up" | "partial" | "down";
    uptime_seconds: number | null;
    incoming_bytes: number | null;
    outgoing_bytes: number | null;
    phase2: { name: string | null; status: "up" | "down" }[];
}

/** A dial-up server with everyone connected to it grouped in one row. */
export interface DialUpTunnel {
    name: string;
    kind: "dial-up";
    /** Built with FortiOS's FortiClient wizard: remote access for people. */
    forticlient: boolean;
    connections: number;
    incoming_bytes: number | null;
    outgoing_bytes: number | null;
}

export type Tunnel = SiteToSiteTunnel | DialUpTunnel;

export interface Tunnels {
    /** Site-to-site tunnels only: a dial-up nobody is connected to is not down. */
    up: number;
    total: number;
    items: Tunnel[];
}

const EMPTY = "—";

const SITE_TO_SITE_STATUS_VARIANT = {
    up: "success",
    partial: "warning",
    down: "error",
} as const;

const phase2Up = (tunnel: SiteToSiteTunnel) => tunnel.phase2.filter((p) => p.status === "up").length;

const SITE_TO_SITE_STATUS_LABEL: Record<SiteToSiteTunnel["status"], (tunnel: SiteToSiteTunnel) => string> = {
    up: () => "Up",
    partial: (tunnel) => `${phase2Up(tunnel)} of ${tunnel.phase2.length} up`,
    down: () => "Down",
};

/** `3d 4h`, `5h 12m`, `12m`, `40s`. */
export function formatUptime(seconds: number | null): string {
    if (seconds == null) return EMPTY;
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
}

/** "↓ 6.8 MB · ↑ 22.2 MB" — what went through it, when the firewall counts it. */
export function tunnelTraffic(tunnel: Tunnel): string | null {
    if (tunnel.incoming_bytes == null && tunnel.outgoing_bytes == null) return null;
    const bytes = (value: number | null) => (value == null ? EMPTY : formatBytes(value));
    return `↓ ${bytes(tunnel.incoming_bytes)} · ↑ ${bytes(tunnel.outgoing_bytes)}`;
}

/**
 * A site-to-site tunnel reads from its phase 2s — "1 of 2 up" when only some are. A
 * dial-up tunnel says how many are connected, in a neutral tag: zero is idle, not a fault.
 */
export function TunnelStatus({ tunnel }: { tunnel: Tunnel }) {
    if (tunnel.kind === "dial-up") {
        return <GenericTag variant="default" label={`${tunnel.connections} connected`} size="small" />;
    }
    return (
        <GenericTag
            variant={SITE_TO_SITE_STATUS_VARIANT[tunnel.status]}
            label={SITE_TO_SITE_STATUS_LABEL[tunnel.status](tunnel)}
            size="small"
        />
    );
}
